import { useParams, Link, useNavigate } from 'react-router-dom'
import { usePeptides } from '../context/PeptidesContext.jsx'
import { DISCLAIMER } from '../data/peptides.js'
import { STACKS } from '../data/stacks.js'
import { withBrand } from '../lib/seoTitle.js'
import CalculatorForm from '../components/CalculatorForm.jsx'
import Seo from '../components/Seo.jsx'

// Build a scannable FAQ straight from the profile fields (LLM-friendly Q&A).
function buildFaq(p) {
  const faq = []
  if (p.overview || p.summary) faq.push({ q: `What is ${p.name}?`, a: p.summary || p.overview })
  if (p.mechanism?.length) faq.push({ q: `How does ${p.name} work?`, a: p.mechanism.join(' ') })
  if (p.researchedUses?.length) faq.push({ q: `What is ${p.name} used for?`, a: p.researchedUses.join('; ') + '.' })
  if (p.protocol?.dose) {
    faq.push({
      q: `What is a typical ${p.name} dose?`,
      a: [p.protocol.dose, p.protocol.frequency, p.protocol.route].filter(Boolean).join(', ') +
        '. This is a commonly cited figure for context only, not medical advice.',
    })
  }
  if (p.reconstitution) faq.push({ q: `How do you reconstitute ${p.name}?`, a: p.reconstitution })
  if (p.halfLife) faq.push({ q: `What is the half-life of ${p.name}?`, a: p.halfLife })
  return faq
}

export default function PeptideDetail() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const { peptides, getBySlug, loading } = usePeptides()
  const p = getBySlug(slug)
  const faq = p ? buildFaq(p) : []
  const faqLd = p && {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    name: `${p.name} — peptide guide`,
    mainEntity: faq.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  }

  if (loading && !p) {
    return (
      <main className="page"><div className="container center" style={{ padding: '60px 0' }}><div className="spinner" /></div></main>
    )
  }
  if (!p) {
    return (
      <main className="page">
        <div className="container center" style={{ padding: '60px 0' }}>
          <h1 className="section-title">Peptide not found</h1>
          <Link to="/peptides" className="btn btn-primary">Back to library</Link>
        </div>
      </main>
    )
  }

  const idx = peptides.findIndex((x) => x.slug === slug)
  const next = peptides[(idx + 1) % peptides.length]

  // Contextual internal links: other peptides in the same category, and any
  // stacks that include this peptide. Both strengthen crawl paths + relevance.
  const related = peptides
    .filter((x) => x.slug !== slug && x.category === p.category)
    .slice(0, 4)
  const inStacks = STACKS.filter((s) => s.peptides.some((pp) => pp.slug === slug))

  const openInCalculator = () => {
    if (!p.calcPreset) return
    navigate('/calculator', { state: { calc: { ...p.calcPreset, name: p.name, syringe: 's100' } } })
  }

  // Rows for the "at a glance" table (skip blanks).
  const facts = [
    ['Name', p.name],
    ['Also known as', p.aka?.length ? p.aka.join(', ') : null],
    ['Category', p.category],
    ['Type', p.type === 'amino' ? 'Amino / injectable' : 'Peptide'],
    ['Sequence', p.sequence],
    ['Typical dose', p.protocol?.dose],
    ['Frequency', p.protocol?.frequency],
    ['Route', p.protocol?.route],
    ['Half-life', p.halfLife],
  ].filter(([, v]) => v)

  return (
    <main className="page">
      <Seo
        title={withBrand(`${p.name} — ${p.calcPreset ? 'Dosage, Chart & Calculator' : 'Benefits, Uses & Dosage'}`)}
        description={`${p.summary} ${p.calcPreset
          ? `Dosing, reconstitution and a free units calculator for ${p.name}.`
          : `${p.name} benefits, common research uses, and how it's used.`} Educational, not medical advice.`}
        noindex={!!p.noindex}
        jsonLd={faqLd}
        image="/og/peptide.png"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Peptide Library', path: '/peptides' },
          { name: p.name, path: `/peptides/${slug}` },
        ]}
      />
      <div className="container page-narrow">
        <Link to="/peptides" className="pep-back">← Peptide library</Link>

        <div className="pep-hero">
          <span className="pep-cat">{p.category}</span>
          <h1 className="pep-title">{p.name}</h1>
          {p.aka?.length > 0 && <p className="pep-aka">Also known as: {p.aka.join(' · ')}</p>}
          <p className="pep-lede">{p.summary}</p>
          {p.calcPreset && (
            <button className="btn btn-primary btn-sm" onClick={openInCalculator}>🧮 Open in calculator</button>
          )}
        </div>

        {/* at-a-glance table (scannable + LLM-friendly) */}
        <Section title={`${p.name} at a glance`}>
          <table className="pep-table">
            <tbody>
              {facts.map(([k, v]) => (
                <tr key={k}>
                  <th scope="row">{k}</th>
                  <td className={k === 'Sequence' ? 'mono' : ''}>{v}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>

        <Section title="Overview">
          <p>{p.overview}</p>
        </Section>

        <Section title="How it’s thought to work">
          <ul className="pep-list">{p.mechanism.map((m, i) => <li key={i}>{m}</li>)}</ul>
        </Section>

        <Section title="Researched uses">
          <ul className="pep-list">{p.researchedUses.map((u, i) => <li key={i}>{u}</li>)}</ul>
        </Section>

        <Section title="Commonly cited protocol">
          <table className="pep-table">
            <tbody>
              <tr><th scope="row">Typical dose</th><td>{p.protocol.dose}</td></tr>
              <tr><th scope="row">Frequency</th><td>{p.protocol.frequency}</td></tr>
              <tr><th scope="row">Route</th><td>{p.protocol.route}</td></tr>
              {p.protocol.notes && <tr><th scope="row">Notes</th><td>{p.protocol.notes}</td></tr>}
            </tbody>
          </table>
        </Section>

        <Section title="Reconstitution">
          <p>{p.reconstitution}</p>
        </Section>

        {p.calcPreset && (
          <Section title={`${p.name} dosage calculator`}>
            <p className="soft" style={{ marginTop: 0 }}>
              The calculator below is pre-filled with {p.name}’s commonly cited vial size and dose. Adjust the
              vial amount, bacteriostatic water, or dose to match your own vial — it will show the exact
              insulin-syringe units to draw, the concentration, and how many doses the vial holds.
            </p>
            <CalculatorForm initial={{ ...p.calcPreset, name: p.name }} />
            <p className="muted small" style={{ marginTop: 10 }}>
              See the full <Link to={`/charts/${slug}`}>{p.name} dosing chart</Link> (units for every dose &amp; water amount),
              or open the full <Link to="/calculator">peptide calculator →</Link>
            </p>
          </Section>
        )}

        <Section title="Cautions">
          <ul className="pep-list warn-list">{p.cautions.map((c, i) => <li key={i}>{c}</li>)}</ul>
        </Section>

        {faq.length > 0 && (
          <Section title={`${p.name} FAQ`}>
            <div className="pep-faq">
              {faq.map((f, i) => (
                <details className="faq-item" key={i} open>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </Section>
        )}

        {inStacks.length > 0 && (
          <Section title={`Stacks with ${p.name}`}>
            <div className="pep-related">
              {inStacks.map((s) => (
                <Link key={s.slug} to={`/stacks/${s.slug}`} className="pep-related-card">
                  <span className="pep-related-ico" aria-hidden="true">{s.icon}</span>
                  <span className="pep-related-body">
                    <span className="pep-related-name">{s.name}</span>
                    <span className="pep-related-desc">{s.summary}</span>
                  </span>
                </Link>
              ))}
            </div>
          </Section>
        )}

        {related.length > 0 && (
          <Section title={`Related ${p.category ? p.category.toLowerCase() : ''} peptides`.replace(/\s+/g, ' ')}>
            <div className="pep-related">
              {related.map((r) => (
                <Link key={r.slug} to={`/peptides/${r.slug}`} className="pep-related-card">
                  <span className="pep-related-body">
                    <span className="pep-related-name">{r.name}</span>
                    <span className="pep-related-desc">{r.summary}</span>
                  </span>
                </Link>
              ))}
            </div>
          </Section>
        )}

        <div className="pep-disclaimer">⚠️ {DISCLAIMER}</div>

        <div className="pep-next">
          <span className="muted small">Next</span>
          <Link to={`/peptides/${next.slug}`} className="btn btn-ghost btn-sm">{next.name} →</Link>
        </div>
      </div>
    </main>
  )
}

function Section({ title, children }) {
  return (
    <section className="pep-section">
      <h2 className="pep-h2">{title}</h2>
      {children}
    </section>
  )
}
